import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { QueryScope, PanelDataMap, PanelFilter } from '../interfaces/panel-data';
import { PanelDataService } from '../model/PanelDataService';
import { buildPanelFilters, serializePanelFilters } from '../utils/panel-filters';

export interface UsePanelDataOptions {
  /** Extra panel-specific filters, merged with those derived from the scope and time window. */
  filters?: PanelFilter[];
  enabled?: boolean;
  timeWindow?: string;
}

const panelDataService = new PanelDataService();

export function usePanelData<T extends keyof PanelDataMap>(
  panelType: T,
  scope: QueryScope,
  options?: UsePanelDataOptions
): UseQueryResult<PanelDataMap[T]> {
  const filterParams = serializePanelFilters(buildPanelFilters(scope, options));

  return useQuery({
    // Keyed on the serialised filters rather than on the individual inputs, so the key is exactly
    // what the request is: two calls that resolve to the same filters share a cache entry.
    queryKey: ['panel-data', panelType, scope.type, filterParams],
    queryFn: async () => {
      const response = await panelDataService.queryPanelData({
        panelType,
        scopeType: scope.type,
        filterParams,
      });
      // A malformed payload is not validated here: the panels guard themselves with an
      // ErrorBoundary, which catches every render-time failure rather than only the fields a
      // validator thought to check.
      return JSON.parse(response.data) as PanelDataMap[T];
    },
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000,
    // The filters are part of the key, so changing one is a new query with no cached data — and without
    // this, `data` goes undefined for the round trip. Any panel that renders its own filter controls from
    // `data.filters` then unmounts the control the user is holding, mid-interaction, and remounts it a
    // second later with focus lost. Keeping the previous data leaves the controls in place and lets the
    // body show a loading state under them, which is what a filter change should look like.
    keepPreviousData: true,
  });
}
